import React, { useMemo } from 'react';
import { FiHelpCircle, FiCheck, FiAlertCircle } from 'react-icons/fi';
import { FaFlask } from 'react-icons/fa';
import './StepLabQuestions.css';

export default function StepLabQuestions({
  questions,
  aoeAnswers,
  setAoeAnswers,
  missingAoeQuestions = [],
  formError,
}) {
  const list = Array.isArray(questions) ? questions : [];

  const missingKeys = useMemo(() => {
    return new Set(missingAoeQuestions.map((q) => `${q.markerId}:${q.questionId}`));
  }, [missingAoeQuestions]);

  const setAnswer = (key, value) => {
    setAoeAnswers((prev) => ({ ...prev, [key]: value }));
  };

  const toggleMultiChoice = (key, code) => {
    setAoeAnswers((prev) => {
      const current = String(prev?.[key] || '')
        .split(',')
        .map((c) => c.trim())
        .filter(Boolean);
      const next = current.includes(code)
        ? current.filter((c) => c !== code)
        : [...current, code];
      return { ...prev, [key]: next.join(',') };
    });
  };

  if (list.length === 0) {
    return (
      <div className="lcw-card">
        <div className="lcw-card-body">
          <div className="lcw-info lcw-info--flush">
            No additional lab questions are required for this test.
          </div>
        </div>
      </div>
    );
  }

  const getPlaceholder = (q) => {
    if (q.placeholder) return q.placeholder;
    if (q.isNumeric) return 'e.g. 42';
    if (q.label?.toLowerCase().includes('specimen')) return 'e.g. Blood';
    return 'Enter answer…';
  };

  return (
    <div>
      <p className="lcw-step-description">
        Your lab requires these details before your order can be processed. Each question is tied to a specific test in your panel.
      </p>

      <div className="lcw-aoe-list">
        {list.map((q) => {
          const key = `${q.markerId}:${q.questionId}`;
          const value = aoeAnswers?.[key] || '';
          const selectedCodes = String(value || '')
            .split(',')
            .map((c) => c.trim())
            .filter(Boolean);

          const usesChoiceChips = q.isChoice && q.options.length <= 5;
          const usesMultiChoiceChips = q.isMultiChoice && q.options.length <= 5;
          const labelTarget = usesChoiceChips || usesMultiChoiceChips ? undefined : `aoe-${key}`;
          const isInvalid = formError && missingKeys.has(key);

          return (
            <div
              className="lcw-aoe-card"
              key={key}
              data-aoe-key={key}
              data-invalid={isInvalid}
            >
              <div className="lcw-aoe-card-header">
                {q.biomarkerName && (
                  <div className="lcw-aoe-biomarker-badge" title={q.biomarkerName}>
                    <FaFlask size={12} className="lcw-aoe-badge-icon" />
                    <span className="lcw-aoe-badge-text">{q.biomarkerName}</span>
                  </div>
                )}
                {q.required ? (
                  <div className="lcw-aoe-required">Required</div>
                ) : (
                  <div className="lcw-aoe-optional">Optional</div>
                )}
              </div>

              <label
                id={`aoe-label-${key}`}
                className="lcw-aoe-question-label"
                htmlFor={labelTarget}
                title={q.label}
              >
                {q.label}
              </label>

              {q.helperText && (
                <div className="lcw-aoe-helper" title={String(q.helperText)}>
                  <FiHelpCircle size={14} />
                  <span>{q.helperText}</span>
                </div>
              )}

              {q.isChoice && (
                q.options.length <= 5 ? (
                  <div
                    className="lcw-aoe-chip-group"
                    role="radiogroup"
                    aria-labelledby={`aoe-label-${key}`}
                    aria-required={q.required}
                  >
                    {q.options.map((opt) => {
                      const isSelected = value === opt.code;
                      return (
                        <button
                          key={opt.code}
                          type="button"
                          className={`lcw-aoe-chip ${isSelected ? 'lcw-aoe-chip--selected' : ''}`}
                          onClick={() => setAnswer(key, opt.code)}
                          role="radio"
                          aria-checked={isSelected}
                        >
                          {isSelected && <FiCheck size={14} className="lcw-aoe-chip-icon" />}
                          <span>{opt.label || opt.value}</span>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <select
                    id={`aoe-${key}`}
                    className="lcw-input"
                    value={value}
                    onChange={(e) => setAnswer(key, e.target.value)}
                    aria-required={q.required}
                  >
                    <option value="">Select…</option>
                    {q.options.map((opt) => (
                      <option key={opt.code} value={opt.code}>{opt.label || opt.value}</option>
                    ))}
                  </select>
                )
              )}

              {q.isMultiChoice && (
                q.options.length <= 5 ? (
                  <div
                    className="lcw-aoe-chip-group"
                    role="group"
                    aria-labelledby={`aoe-label-${key}`}
                    aria-required={q.required}
                  >
                    {q.options.map((opt) => {
                      const isSelected = selectedCodes.includes(opt.code);
                      return (
                        <button
                          key={opt.code}
                          type="button"
                          className={`lcw-aoe-chip ${isSelected ? 'lcw-aoe-chip--selected' : ''}`}
                          onClick={() => toggleMultiChoice(key, opt.code)}
                          aria-pressed={isSelected}
                        >
                          {isSelected && <FiCheck size={14} className="lcw-aoe-chip-icon" />}
                          <span>{opt.label || opt.value}</span>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="lcw-aoe-checkboxes">
                    {q.options.map((opt) => {
                      const isSelected = selectedCodes.includes(opt.code);
                      return (
                        <label className="lcw-consent-row" key={opt.code}>
                          <input
                            type="checkbox"
                            className="lcw-consent-check"
                            checked={isSelected}
                            onChange={() => toggleMultiChoice(key, opt.code)}
                          />
                          <span className="lcw-consent-label">{opt.label || opt.value}</span>
                        </label>
                      );
                    })}
                  </div>
                )
              )}

              {q.isNumeric && (
                <>
                  <input
                    id={`aoe-${key}`}
                    type="number"
                    className="lcw-input"
                    value={value}
                    onChange={(e) => setAnswer(key, e.target.value)}
                    placeholder={getPlaceholder(q)}
                    aria-required={q.required}
                  />
                  {q.constraint && (
                    <div className="lcw-field-note">{String(q.constraint)}</div>
                  )}
                </>
              )}

              {q.isText && (
                <input
                  id={`aoe-${key}`}
                  type="text"
                  className="lcw-input"
                  value={value}
                  onChange={(e) => setAnswer(key, e.target.value)}
                  placeholder={getPlaceholder(q)}
                  aria-required={q.required}
                />
              )}

              {isInvalid && (
                <div className="lcw-aoe-error-msg">
                  <FiAlertCircle size={14} /> This answer is required.
                </div>
              )}
            </div>
          );
        })}
      </div>

      {formError && list.length > 0 && (
        <div className="lcw-error lcw-error-top lcw-aoe-form-error">{formError}</div>
      )}
    </div>
  );
}
